import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import Decimal from 'decimal.js';
import { defaultBillingDayOfWeek } from '~/constants';
import { utils } from '~/utils';
import { DiscountDTOType, serviceCostModel, serviceModel, serviceTemplateBundleModel, timeModel } from '..';
import { ApprovalMethodDateModel } from '../ApprovalMethodDateModel';
import { BillingDayOfMonthModel } from '../BillingDayOfMonth';
import { LimitedTimeDiscountOfferModel } from '../LimitedTimeDiscountOfferModel';
import * as serviceBillingModel from '../service-billing';
import { makeBillUpfrontState, makeOnApprovalBilling, makeOnDemandBilling, makeOneTimeInStagesBilling, makeOneTimeManualBilling, makeOngoingBilling, makeRepeatableManualBilling } from '../service-billing/makers';
import { matchBilling } from '../service-template';
import { ServiceIntegrationsModel } from '../ServiceIntegrations';
import { fromDateStampObject, toDateStampObject } from '../time';
import * as makers from './makers';
import { ServiceStatusType, EffectiveDateSource } from './model';
import * as selectors from './selectors';
export function includedServiceFromDTO(dto) {
    var _dto_terms;
    return _object_spread_props(_object_spread({}, dto), {
        terms: (_dto_terms = dto.terms) !== null && _dto_terms !== void 0 ? _dto_terms : '',
        rate: new Decimal(dto.rate).div(100),
        integrations: ServiceIntegrationsModel.fromDTO(dto.integrations)
    });
}
export function includedServiceToDTO(service) {
    return _object_spread_props(_object_spread({}, service), {
        rate: service.rate.mul(100).toNumber(),
        integrations: service.integrations.toJSON()
    });
}
function getStartBillingAtFromDTO(statusDTO) {
    switch(statusDTO.type){
        case ServiceStatusType.AwaitingApproval:
            {
                const { effectiveDateSource, effectiveDate } = statusDTO.awaitingApproval;
                return effectiveDateSource === EffectiveDateSource.User && effectiveDate ? fromDateStampObject(effectiveDate) : null;
            }
        case ServiceStatusType.Amended:
            {
                const { effectiveDateSource, effectiveDate } = statusDTO.amended;
                return effectiveDateSource === EffectiveDateSource.User && effectiveDate ? fromDateStampObject(effectiveDate) : null;
            }
        case ServiceStatusType.Approved:
            {
                var _statusDTO_approved, _statusDTO_approved1;
                const effectiveDateSource = (_statusDTO_approved = statusDTO.approved) === null || _statusDTO_approved === void 0 ? void 0 : _statusDTO_approved.effectiveDateSource;
                const effectiveDate = (_statusDTO_approved1 = statusDTO.approved) === null || _statusDTO_approved1 === void 0 ? void 0 : _statusDTO_approved1.effectiveDate;
                return effectiveDateSource === EffectiveDateSource.User && effectiveDate ? fromDateStampObject(effectiveDate) : null;
            }
        case ServiceStatusType.Completed:
            {
                const { effectiveDateSource, effectiveDate } = statusDTO.completed;
                return effectiveDateSource === EffectiveDateSource.User && effectiveDate ? fromDateStampObject(effectiveDate) : null;
            }
        case ServiceStatusType.Canceled:
        case ServiceStatusType.Terminated:
            return null;
    }
}
export function fromDTO(service) {
    const status = statusFromDTO(service.status);
    const startBillingAt = getStartBillingAtFromDTO(service.status);
    const billing = serviceBillingModel.fromDTO(service.billing, selectors.isAwaitingApproval(status), {
        startBillingAt
    });
    const limitedTimeDiscountOffer = service.status.type === ServiceStatusType.AwaitingApproval ? service.status.awaitingApproval.limitedTimeDiscountOffer : undefined;
    if (limitedTimeDiscountOffer) {
        service.cost.discount = limitedTimeDiscountOffer.discount;
    }
    const cost = serviceCostModel.fromDTO(service.cost);
    if (billing != null) {
        return makers.makeService(_object_spread_props(_object_spread({}, service), {
            billing,
            cost,
            status,
            participation: !service.participation.type ? makers.makeMandatoryParticipation() : service.participation,
            integrations: ServiceIntegrationsModel.fromDTO(service.integrations),
            bundle: service.bundle ? {
                services: service.bundle.services.map((service)=>includedServiceFromDTO(service))
            } : undefined
        }));
    }
}
export function toDTO(s) {
    const limitedTimeDiscountOffer = serviceModel.limitedTimeDiscountOffer(s);
    const dto = _object_spread({
        id: s.id,
        name: s.name,
        description: s.description,
        terms: s.terms,
        serviceTemplateId: s.serviceTemplateId,
        participation: s.participation,
        isBillable: s.isBillable,
        cost: serviceCostModel.toDTO({
            cost: s.cost,
            shouldResetDiscount: !!limitedTimeDiscountOffer
        }),
        billing: serviceBillingModel.toDTO(s.billing),
        status: statusToDTO(s.status, s.cost),
        source: s.source
    }, s.bundle && {
        bundle: {
            services: s.bundle.services.map((service)=>includedServiceToDTO(service))
        }
    });
    if (s.integrations.hasIntegrations()) {
        dto.integrations = s.integrations.toJSON();
    }
    return dto;
}
export function fromServiceTemplate(template, source) {
    let context = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
    const { id, name, description, terms, cost, integrations, bundle } = template;
    const status = makers.makeAwaitingApprovalStatus();
    const billing = matchBilling(template.billing, {
        ongoing: (b)=>{
            var _context_billingDayOfMonth, _context_billingDayOfWeek, _context_isPaused, _context_prorateFirstInvoice, _context_isBilledUpfront;
            return makeOngoingBilling(b.recurrencePeriod, {
                billingDayOfMonth: (_context_billingDayOfMonth = context.billingDayOfMonth) !== null && _context_billingDayOfMonth !== void 0 ? _context_billingDayOfMonth : BillingDayOfMonthModel.make(),
                billingDayOfWeek: (_context_billingDayOfWeek = context.billingDayOfWeek) !== null && _context_billingDayOfWeek !== void 0 ? _context_billingDayOfWeek : defaultBillingDayOfWeek,
                isPaused: (_context_isPaused = context.isPaused) !== null && _context_isPaused !== void 0 ? _context_isPaused : false,
                isProrated: (_context_prorateFirstInvoice = context.prorateFirstInvoice) !== null && _context_prorateFirstInvoice !== void 0 ? _context_prorateFirstInvoice : false,
                isBilledUpfront: (_context_isBilledUpfront = context.isBilledUpfront) !== null && _context_isBilledUpfront !== void 0 ? _context_isBilledUpfront : false,
                billUpfrontState: context.isBilledUpfront ? makeBillUpfrontState(context.skipBillingOnAcceptance || !!context.isPaused) : undefined,
                isAwaitingApproval: status.type === ServiceStatusType.AwaitingApproval
            });
        },
        repeatableManual: (b)=>makeRepeatableManualBilling(b.recurrencePeriod),
        oneTimeManual: ()=>makeOneTimeManualBilling(),
        oneTimeInStages: (b)=>makeOneTimeInStagesBilling(b.oneTimeInStages),
        onApproval: ()=>makeOnApprovalBilling(),
        onDemand: (b)=>makeOnDemandBilling(b.preapprovalPeriod)
    });
    return makers.makeService({
        name,
        description,
        terms,
        serviceTemplateId: id,
        integrations,
        status,
        source,
        cost,
        billing,
        bundle: bundle ? serviceTemplateBundleModel.toServiceBundle(bundle) : undefined
    });
}
export function statusFromDTO(dto) {
    if (dto.type === ServiceStatusType.AwaitingApproval) {
        const limitedTimeDiscountOffer = dto.awaitingApproval.limitedTimeDiscountOffer ? LimitedTimeDiscountOfferModel.fromDTO(dto.awaitingApproval.limitedTimeDiscountOffer) : undefined;
        return {
            type: dto.type,
            awaitingApproval: _object_spread({
                addedOn: timeModel.fromDateStampObject(dto.awaitingApproval.addedOn),
                approvalMethod: ApprovalMethodDateModel.fromDTO(dto.awaitingApproval.approvalMethod),
                note: dto.awaitingApproval.note,
                effectiveDateSource: dto.awaitingApproval.effectiveDateSource || null,
                effectiveDate: dto.awaitingApproval.effectiveDate ? timeModel.fromDateStampObject(dto.awaitingApproval.effectiveDate) : null
            }, limitedTimeDiscountOffer && {
                limitedTimeDiscountOffer
            })
        };
    } else if (dto.type === ServiceStatusType.Amended) {
        return {
            type: dto.type,
            date: timeModel.fromDateStampObject(dto.date),
            amended: {
                amendedOn: timeModel.fromDateStampObject(dto.amended.amendedOn),
                approvalMethod: ApprovalMethodDateModel.fromDTO(dto.amended.approvalMethod),
                note: dto.amended.note,
                changes: changesFromDTO(dto.amended.changes),
                effectiveDateSource: dto.amended.effectiveDateSource || null,
                effectiveDate: dto.amended.effectiveDate ? timeModel.fromDateStampObject(dto.amended.effectiveDate) : null
            }
        };
    } else if (dto.type === ServiceStatusType.Canceled) {
        return {
            type: dto.type
        };
    } else if (dto.type === ServiceStatusType.Completed) {
        const obj = {
            type: dto.type,
            date: timeModel.fromDateStampObject(dto.date),
            completed: {
                effectiveDate: dto.completed.effectiveDate ? timeModel.fromDateStampObject(dto.completed.effectiveDate) : null,
                effectiveDateSource: dto.completed.effectiveDateSource || null
            }
        };
        if (dto.completed.changes) {
            obj.completed.changes = changesFromDTO(dto.completed.changes);
        }
        return obj;
    } else if (dto.type === ServiceStatusType.Approved) {
        var _dto_approved, _dto_approved1;
        return {
            type: dto.type,
            date: timeModel.fromDateStampObject(dto.date),
            approved: {
                effectiveDateSource: ((_dto_approved = dto.approved) === null || _dto_approved === void 0 ? void 0 : _dto_approved.effectiveDateSource) || null,
                effectiveDate: ((_dto_approved1 = dto.approved) === null || _dto_approved1 === void 0 ? void 0 : _dto_approved1.effectiveDate) ? timeModel.fromDateStampObject(dto.approved.effectiveDate) : null
            }
        };
    } else {
        return {
            type: dto.type,
            date: timeModel.fromDateStampObject(dto.date)
        };
    }
}
export function statusToDTO(status, cost) {
    return selectors.matchStatus(status, {
        awaitingApproval: (status)=>({
                type: status.type,
                awaitingApproval: _object_spread({
                    addedOn: timeModel.toDateStampObject(status.awaitingApproval.addedOn),
                    approvalMethod: status.awaitingApproval.approvalMethod.toDTO(),
                    note: status.awaitingApproval.note
                }, status.awaitingApproval.limitedTimeDiscountOffer && cost.discount && {
                    limitedTimeDiscountOffer: {
                        discount: cost.discount.toDTO(),
                        expiration: status.awaitingApproval.limitedTimeDiscountOffer.expiration
                    }
                }, status.awaitingApproval.effectiveDate && {
                    effectiveDate: timeModel.toDateStampObject(status.awaitingApproval.effectiveDate)
                }, status.awaitingApproval.effectiveDateSource && {
                    effectiveDateSource: status.awaitingApproval.effectiveDateSource
                })
            }),
        amended: (status)=>({
                type: status.type,
                date: timeModel.toDateStampObject(status.date),
                amended: _object_spread({
                    amendedOn: timeModel.toDateStampObject(status.amended.amendedOn),
                    approvalMethod: status.amended.approvalMethod.toDTO(),
                    note: status.amended.note,
                    changes: changesToDTO(status.amended.changes)
                }, status.amended.effectiveDate && {
                    effectiveDate: timeModel.toDateStampObject(status.amended.effectiveDate)
                }, status.amended.effectiveDateSource && {
                    effectiveDateSource: status.amended.effectiveDateSource
                })
            }),
        canceled: (status)=>({
                type: status.type
            }),
        completed: (status)=>{
            const dto = {
                type: status.type,
                date: timeModel.toDateStampObject(status.date),
                completed: {}
            };
            if (status.completed.changes) {
                dto.completed.changes = changesToDTO(status.completed.changes);
            }
            return dto;
        },
        terminated: (status)=>({
                type: status.type,
                date: timeModel.toDateStampObject(status.date)
            }),
        approved: (status)=>{
            var _status_approved_effectiveDateSource;
            var _status_approved;
            return _object_spread({
                type: status.type,
                date: timeModel.toDateStampObject(status.date)
            }, ((_status_approved = status.approved) === null || _status_approved === void 0 ? void 0 : _status_approved.effectiveDate) && {
                approved: {
                    effectiveDate: timeModel.toDateStampObject(status.approved.effectiveDate),
                    effectiveDateSource: (_status_approved_effectiveDateSource = status.approved.effectiveDateSource) !== null && _status_approved_effectiveDateSource !== void 0 ? _status_approved_effectiveDateSource : undefined
                }
            });
        }
    });
}
const getPricingType = (cost, forceZeroCap)=>{
    if (serviceCostModel.isFixedPrice(cost)) {
        return {
            type: cost.pricingType,
            fixed: {
                price: utils.dollarsToCents(cost.price)
            },
            variable: null,
            range: null
        };
    }
    if (serviceCostModel.isVariablePrice(cost)) {
        return {
            type: cost.pricingType,
            fixed: null,
            variable: {
                unitPrice: utils.dollarsToCents(cost.price),
                cap: cost.unitCap != null ? utils.dollarsToCents(cost.unitCap) : forceZeroCap ? 0 : null,
                name: cost.unitName
            },
            range: null
        };
    }
    return {
        type: cost.pricingType,
        fixed: null,
        variable: null,
        range: {
            minPrice: cost.minPrice && utils.dollarsToCents(cost.minPrice),
            maxPrice: utils.dollarsToCents(cost.maxPrice)
        }
    };
};
export function toDBService(service) {
    let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    const { cost } = service;
    const startBillingAt = serviceModel.startBillingAt(service);
    return _object_spread({
        id: service.id,
        name: service.name,
        description: service.description,
        terms: service.terms,
        billedUpon: serviceBillingModel.toDBServiceBillingTrigger(service.billing),
        cost: {
            pricingType: getPricingType(cost, options.forceZeroCap),
            discount: (()=>{
                const originalDiscount = cost.discount || cost.expiredDiscount;
                return originalDiscount ? originalDiscount.toDBDTO() : {
                    type: DiscountDTOType.None
                };
            })(),
            priceIncrease: cost.priceIncrease.toDB()
        },
        note: options.note || null,
        status: selectors.isTerminated(service.status) || selectors.isCanceled(service.status) ? service.status.type : undefined,
        serviceTemplateId: service.serviceTemplateId,
        participation: !service.participation.type ? makers.makeMandatoryParticipation() : service.participation,
        bundle: service.bundle && {
            services: service.bundle.services.map(includedServiceToDTO)
        }
    }, startBillingAt && {
        effectiveDate: toDateStampObject(startBillingAt)
    });
}
function changesFromDTO(changesDTO) {
    return _object_spread_props(_object_spread({}, changesDTO), {
        billing: changesDTO.billing && serviceBillingModel.fromDTO(changesDTO.billing) || null,
        cost: changesDTO.cost && serviceCostModel.fromDTO(changesDTO.cost),
        bundle: changesDTO.bundle && {
            services: changesDTO.bundle.services.map(includedServiceFromDTO)
        }
    });
}
function changesToDTO(changes) {
    return _object_spread_props(_object_spread({}, changes), {
        cost: changes.cost ? serviceCostModel.toDTO({
            cost: changes.cost,
            shouldResetDiscount: false
        }) : null,
        billing: changes.billing ? serviceBillingModel.toDTO(changes.billing) : null,
        bundle: changes.bundle && {
            services: changes.bundle.services.map(includedServiceToDTO)
        }
    });
}
